import React from "react";
import { Icon } from "@cadmakers-ui/icons";

import { ThunkDispatch } from "redux-thunk";
import { showSnack } from "react-redux-snackbar";
import { connect } from "react-redux";
import { Button } from "reactstrap";
import { FeedbackCategory } from "../../../../Feedback";
import styles from "../../../Styles/SubscribeToAppButton.module.scss";
import { ApiRequest, IAppState } from "../../../../Common";
import { endpoints } from "../../../Constants/endpoints";
import ResponseHandler from "../../../ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";
import iconStyles from "../../../../Common/Styles/Icons.module.scss";

type Props = {
  appId: string;
  appName?: string;
  isSubscribed?: boolean;
  category?: FeedbackCategory;
  isLoading: boolean;
  onSubscribe: (category?: FeedbackCategory) => void;
  onUnsubscribe: () => void;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, {}, any>): DispatchProps {
  return {
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class SubscribeToAppButton extends React.Component<Props & DispatchProps> {
  subscribeLock: boolean;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.onSubscribeClick = this.onSubscribeClick.bind(this);
    this.onUnsubscribeClick = this.onUnsubscribeClick.bind(this);

    this.subscribeLock = false;
  }

  onSubscribeClick() {
    if (!this.claimSubscribeLock()) {
      return;
    }

    ApiRequest.fromPath(endpoints.subscriptions.subscribe(this.props.appId))
      .withAuthHeader()
      .post()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            // success
            this.props.onSubscribe();
          },
          (result: ResponseResult) => {
            // error
            this.showErrorSnackbar(response.status, `Failed to subscribe: ${result.error.message}`);
          },
          () => {
            // fatal
            this.showErrorSnackbar(response.status, "Failed to subscribe: Unknown Error");
          }
        );
        this.releaseSubscribeLock();
      });
  }

  onUnsubscribeClick() {
    if (!this.claimSubscribeLock()) {
      return;
    }

    ApiRequest.fromPath(endpoints.subscriptions.unsubscribe(this.props.appId))
      .withAuthHeader()
      .post()
      .then((response: Response) => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult) => {
            // success
            this.props.onUnsubscribe();
          },
          (result: ResponseResult) => {
            // error
            this.showErrorSnackbar(
              response.status,
              `Failed to unsubscribe: ${result.error.message}`
            );
          },
          () => {
            // fatal
            this.showErrorSnackbar(response.status, "Failed to unsubscribe: Unknown Error");
          }
        );
        this.releaseSubscribeLock();
      });
  }

  claimSubscribeLock() {
    if (!this.subscribeLock) {
      this.subscribeLock = true;
      return true;
    }
    return false;
  }

  releaseSubscribeLock() {
    this.subscribeLock = false;
  }

  showErrorSnackbar(errorCode: number, errorDescription: string) {
    this.props.showSnack("APP_SUBSCRIBE_ERROR", {
      label: `${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  render() {
    return (
      <div className={styles.subscribeToAppButtonContainer}>
        {this.props.isLoading && <Button>Loading...</Button>}
        {!this.props.isLoading && (
          <>
            {!!this.props.isSubscribed && (
              <Button
                onClick={this.onUnsubscribeClick}
                className={styles.unsubscribeButton}
                title={`You are subscribed to ${this.props.appName} feedback. You will receive emails when new feedback for this app is created. Click to unsubscribe.`}
              >
                <span>Subscribed</span>
                &nbsp;
                <Icon
                  appName="GeneralLayout"
                  assetName="GENERAL_L_Checkmark_16"
                  className={iconStyles.smallIcon}
                />
              </Button>
            )}
            {!this.props.isSubscribed && (
              <Button
                onClick={this.onSubscribeClick}
                className={styles.subscribeButton}
                title={`Click here to subscribe to ${this.props.appName} feedback. When subscribed, you will receive emails when feedback for this app is created.`}
              >
                Subscribe to {this.props.appName} Feedback
              </Button>
            )}
          </>
        )}
      </div>
    );
  }
}

export default connect(null, mapDispatchToProps)(SubscribeToAppButton);
